import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Link,
  MenuItem,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import ClearIcon from "@material-ui/icons/Clear";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ControlledSelect from "../../Components/ControlledSelect";
import DeviceSearchAutocomplete from "../../Components/DeviceSearchAutocomplete";
import DialogAppBar from "../../Components/DialogAppBar";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";
import { getDevices } from "../../Utils/ApiCalls";
import {
  ECABIN,
  USER_GROUPS,
  checkAdmin,
  checkNewSerialNumber,
  checkRequiredPermissions,
  findValueInText,
  formatClientInfoString,
  formatTimeWithTZOffset,
  getDeviceProfiles,
  getDeviceType,
  getLCLocation,
  getLCState,
  getLatestLifeCycleState,
  getToken,
  getWarehouses,
  lifeCycleStates,
  readClientDataFromLCS,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import Checklist from "../DeviceDetails/Checklist";
import LifeCycleState from "../DeviceDetails/LifeCycleState";
import AddDeviceDialog from "./AddDeviceDialog";
import DeviceDataForm from "./DeviceDataForm";
import ProductionPhotos from "./ProductionPhotos";

const server = EnvSettings.server;

function SelectedDeviceDialog({
  selectedDevice,
  setSelectedDevice,
  setDeviceList,
  setSearchList,
}) {
  const { t } = useTranslation();
  return selectedDevice ? (
    <Dialog
      open={!!selectedDevice}
      onClose={() => {
        setSelectedDevice(undefined);
      }}
      fullScreen
    >
      <DialogContent>
        <DialogAppBar
          header={
            <>
              {" "}
              {selectedDevice.serialNumber}{" "}
              <Link
                style={{ color: "white" }}
                href={
                  (checkAdmin() ? "/cpanel" : "") +
                  "/devices/" +
                  selectedDevice.id
                }
              >
                {"- " + t("viewDevicePage")}
              </Link>
            </>
          }
          onClose={() => {
            setSelectedDevice(undefined);
          }}
        />
        <Grid
          container
          spacing={3}
          style={{
            marginTop: 60,
            marginBottom: 20,
            paddingBottom: 30,
            position: "relative",
          }}
        >
          <Grid item xs={12}>
            <DeviceDataForm
              deviceData={selectedDevice}
              updateLocalDevice={(deviceData) => {
                setSelectedDevice(deviceData);
                setDeviceList((current) =>
                  replaceFormattedDeviceInList(current, deviceData),
                );
                setSearchList((current) =>
                  replaceFormattedDeviceInList(current, deviceData),
                );
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ borderTop: "1px solid #c5c5c5", marginTop: 12 }}
          >
            <Checklist device_id={selectedDevice.id} />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ borderTop: "1px solid #c5c5c5", marginTop: 12 }}
          >
            <ProductionPhotos device_id={selectedDevice.id} />
          </Grid>
          <Grid
            item
            xs={12}
            style={{ borderTop: "1px solid #c5c5c5", marginTop: 12 }}
          >
            <LifeCycleState
              deviceData={selectedDevice}
              updateLocalDevice={(lcs) => {
                const updatedDevice = {
                  ...selectedDevice,
                  LifeCycleStates: lcs,
                };
                setSelectedDevice(updatedDevice);
                setDeviceList((current) =>
                  replaceFormattedDeviceInList(current, updatedDevice),
                );
                setSearchList((current) =>
                  replaceFormattedDeviceInList(current, updatedDevice),
                );
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  ) : null;
}

function formatDeviceData(data) {
  var arr = [];
  data.forEach((element) => {
    var lc =
      element.LifeCycleStates && element.LifeCycleStates.length
        ? getLatestLifeCycleState(element.LifeCycleStates)
        : undefined;
    arr.push({
      id: element.id,
      serialNumber: element.serialNumber ?? "",
      chassisNumber: element.chassisNumber ?? "",
      plugType: element.plugType ?? "",
      color: element.color ?? "",
      model: element.model ?? "",
      version: element.version ?? "",
      deviceName: element.deviceName ?? "",
      location: element.location ?? "",
      macAddress: element.macAddress ?? "",
      teamviewerId: element.teamviewerId ?? "",
      deviceType: getDeviceType(element.serialNumber),
      companyName: element.Company ? element.Company.name : "",
      user: lc?.User?.fullName ?? "",
      latestTimestamp: lc ? formatTimeWithTZOffset(lc?.createdAt, ECABIN) : "",
      currentLCState: lc ? getLCState(lc?.state) : "",
      currentLCLocation: lc ? getLCLocation(lc?.location) : "",
      OrderId: element.OrderId,
      Order: element.Order,
      clientInfo: lc
        ? formatClientInfoString(readClientDataFromLCS(lc), [
            "location",
            "contactName",
            "companyName",
          ])
        : "",
      note: lc?.Notes?.length ? lc?.Notes[0].text : "",
      LifeCycleStates: element.LifeCycleStates,
    });
  });
  return arr;
}

function replaceFormattedDeviceInList(list, deviceData) {
  if (!list || !deviceData) {
    return list;
  }
  const formattedDevice = formatDeviceData([deviceData])[0];
  return list.map((device) =>
    device.id === formattedDevice.id ? formattedDevice : device,
  );
}

export default function Production(props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [deviceList, setDeviceList] = useState();
  const [searchList, setSearchList] = useState();
  const [fetchedDeviceCount, setFetchedDeviceCount] = useState();
  const [searchTerms, setSearchTerms] = useState({});
  const [quickFilters, setQuickFilters] = useState({
    hideReservations: false,
    lcStates: [],
    lcWarehouses: [],
    onlyAllocated: false,
  });
  const [showQuickFilters, setShowQuickFilters] = useState(false);
  const [quickLcStateOpen, setQuickLcStateOpen] = useState(false);
  const [quickWarehouseOpen, setQuickWarehouseOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState();
  const [selectedDeviceLoading, setSelectedDeviceLoading] = useState(false);
  const [currentSearchTerms, setCurrentSearchTerms] = useState();
  const [loading, setLoading] = useState(false);
  const [acKey, setACKey] = useState("sdfdsfsd");
  const [add, setAdd] = useState(false);
  const dispatch = useDispatch();
  const [warehouseLocation, setWarehouseLocation] = useState();
  const [deviceProfiles, setDeviceProfiles] = useState([]);
  const deviceSearchAbortController = useRef();
  const deviceSearchRequestId = useRef(0);
  const deviceDetailAbortController = useRef();

  useEffect(() => {
    getWarehouses(setWarehouseLocation, dispatch);
    getDeviceProfiles(setDeviceProfiles, dispatch);
  }, [dispatch]);

  const editBoxRef = useRef(null);

  useEffect(() => {
    return () => {
      if (deviceSearchAbortController.current) {
        deviceSearchAbortController.current.abort();
      }
      if (deviceDetailAbortController.current) {
        deviceDetailAbortController.current.abort();
      }
    };
  }, []);

  const showDevice = useCallback(
    (dev) => {
      if (!dev?.id) {
        return;
      }
      if (deviceDetailAbortController.current) {
        deviceDetailAbortController.current.abort();
      }
      setSelectedDeviceLoading(true);
      const abortcontroller = new AbortController();
      deviceDetailAbortController.current = abortcontroller;
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          deviceid: dev.id,
          includeOrder: true,
        }),
        signal: abortcontroller.signal,
      };
      fetch(server + "/get_device_data", requestOptions)
        .then((res) => res.json())
        .then((result) => {
          requestErrorHandler(result, dispatch);
          if (!result.error) {
            setSelectedDevice({
              ...result.device,
              OrderId: result.device.OrderId ?? dev.OrderId,
              Order: result.device.Order || dev.Order,
            });
          }
          setSelectedDeviceLoading(false);
        })
        .catch((error) => {
          if (error?.name !== "AbortError") {
            requestErrorHandler(error, dispatch);
            setSelectedDeviceLoading(false);
          }
        });
    },
    [dispatch],
  );

  const handleAddExit = () => {
    setSearchTerms({
      ...searchTerms,
      serialNumber: undefined,
    });
    setAdd(false);
    setACKey(Math.random());
  };

  const _getDevices = useCallback(
    (ser_val) => {
      if (deviceSearchAbortController.current) {
        deviceSearchAbortController.current.abort();
      }
      const abortcontroller = new AbortController();
      deviceSearchAbortController.current = abortcontroller;
      const requestId = deviceSearchRequestId.current + 1;
      deviceSearchRequestId.current = requestId;
      if (deviceDetailAbortController.current) {
        deviceDetailAbortController.current.abort();
      }
      setLoading(true);
      setSelectedDeviceLoading(false);
      setSearchList();
      setSelectedDevice();
      setCurrentSearchTerms({
        [t("deviceList.serial")]: ser_val || searchTerms.serialNumber,
        [t("deviceList.lcState")]: searchTerms.lcState,
        [t("deviceList.lcLocation")]: searchTerms.lcLocation,
        [t("clientInfo")]: searchTerms.lcClient,
        [t("geolocation")]: searchTerms.geolocation,
        [t("deviceList.chassisNumber")]: searchTerms.chassisNumber,
      });
      getDevices(
        {
          view: "lifecycleList",
          requireLCS: false,
          ...searchTerms,
          ...(ser_val ? { serialNumber: ser_val } : {}),
        },
        (result) => {
          if (requestId !== deviceSearchRequestId.current) {
            return;
          }
          if (!result.error) {
            var a = formatDeviceData(result.devices);
            setDeviceList(a);
            setSearchList(a);
            setFetchedDeviceCount(a.length);
          }
          setLoading(false);
        },
        abortcontroller,
      );
    },
    [searchTerms, t],
  );

  useEffect(() => {
    const handleEnterPress = (e) => {
      if (e.key === "Enter") {
        _getDevices();
      }
    };

    window.addEventListener("keyup", handleEnterPress);
    return () => {
      window.removeEventListener("keyup", handleEnterPress);
    };
  }, [_getDevices]);

  const filterResults = () => {
    const selectedLcStates = quickFilters.lcStates || [];
    const selectedLcStateLabels = selectedLcStates.map(
      (state) => getLCState(state) || state,
    );
    const selectedWarehouses = quickFilters.lcWarehouses || [];
    const selectedWarehouseLabels = selectedWarehouses.map((warehouse) => {
      const match = warehouseLocation?.find((item) => item.value === warehouse);
      return match ? match.label : warehouse;
    });
    setSearchList(
      deviceList &&
        deviceList.filter((contact) => {
          const matchesLcState =
            !selectedLcStateLabels.length ||
            selectedLcStateLabels.includes(contact.currentLCState);
          const matchesWarehouse =
            !selectedWarehouseLabels.length ||
            selectedWarehouseLabels.includes(contact.currentLCLocation);
          const matchesReservation = quickFilters.onlyAllocated
            ? !!contact.OrderId
            : quickFilters.hideReservations
              ? !contact.OrderId
              : true;
          return (
            findValueInText(contact.serialNumber, quickFilters.serialNumber) &&
            findValueInText(
              contact.chassisNumber,
              quickFilters.chassisNumber,
            ) &&
            findValueInText(contact.clientInfo, quickFilters.lcClient) &&
            findValueInText(contact.location, quickFilters.geolocation) &&
            findValueInText(contact.note, quickFilters.note) &&
            findValueInText(contact.color, quickFilters.color) &&
            findValueInText(contact.model, quickFilters.model) &&
            matchesLcState &&
            matchesWarehouse &&
            matchesReservation
          );
        }),
    );
  };

  useEffect(filterResults, [
    quickFilters.serialNumber,
    quickFilters.chassisNumber,
    quickFilters.lcClient,
    quickFilters.geolocation,
    quickFilters.note,
    quickFilters.color,
    deviceList,
    quickFilters.model,
    quickFilters.lcStates,
    quickFilters.lcWarehouses,
    quickFilters.hideReservations,
    quickFilters.onlyAllocated,
    warehouseLocation,
  ]);

  return (
    <Grid container spacing={3}>
      <SelectedDeviceDialog
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        setDeviceList={setDeviceList}
        setSearchList={setSearchList}
      />
      <AddDeviceDialog open={add} exit={handleAddExit} />
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ overflow: "hidden" }}>
          <Grid container spacing={3}>
            <Grid item container spacing={3} xs={12}>
              <Grid item xs={12}>
                <Title>{t("mainSideBar.production")}</Title>
              </Grid>
              <Grid item container xs={12}>
                <Grid
                  item
                  style={{
                    border: "1px solid lightgrey",
                    borderRadius: 5,
                    padding: 5,
                    minWidth: 295,
                  }}
                  xs={12}
                  container
                >
                  <Grid item xs={12}>
                    <Typography style={{ color: "grey" }}>
                      {t("quickSelection")}
                    </Typography>
                  </Grid>
                  {(deviceProfiles.length &&
                    deviceProfiles.map((m, i) => (
                      <Grid item style={{ margin: 5 }} key={i}>
                        <Button
                          onClick={() => {
                            _getDevices(m.prefix);
                          }}
                          variant="outlined"
                        >
                          {m.name}
                        </Button>
                      </Grid>
                    ))) || <CircularProgress />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3} ref={editBoxRef}>
              <DeviceSearchAutocomplete
                label={t("deviceList.serial")}
                onChange={(e, v) => {
                  if (v && typeof v !== "string") {
                    if (v.inputValue && v.inputValue.includes("Add Device")) {
                      var x = checkNewSerialNumber(
                        v.serialNumber,
                        deviceProfiles,
                        t,
                      );
                      if (x) {
                        alert(x);
                        setACKey(Date.now());
                        return;
                      }
                      setAdd(true);
                      setSearchTerms({
                        ...searchTerms,
                        serialNumber: v.serialNumber,
                      });
                      return;
                    }
                    setAdd(false);
                    setSearchTerms({
                      ...searchTerms,
                      serialNumber: v.serialNumber,
                    });
                    return;
                  }
                }}
                freeSearchFunction={(value) => {
                  setSearchTerms({
                    ...searchTerms,
                    serialNumber: value,
                  });
                }}
                allowNewDevices={true}
                // This key hack allows to reset the element after bad input
                key={acKey}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              {warehouseLocation ? (
                <ControlledSelect
                  itemlist={warehouseLocation}
                  label={t("deviceList.lcLocation")}
                  onChange={(e, v) => {
                    if (e) {
                      setSearchTerms({
                        ...searchTerms,
                        lcLocation: e.target.value,
                      });
                      return;
                    }
                    setSearchTerms({
                      ...searchTerms,
                      lcLocation: undefined,
                    });
                  }}
                  deleteButton={true}
                />
              ) : (
                <CircularProgress />
              )}
            </Grid>
            <Grid item container xs={12} sm={3}>
              <ControlledSelect
                itemlist={lifeCycleStates}
                label={t("deviceList.lcState")}
                onChange={(e, v) => {
                  if (e) {
                    setSearchTerms({
                      ...searchTerms,
                      lcState: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    lcState: undefined,
                  });
                }}
                deleteButton={true}
              />
            </Grid>
            <Grid item container xs={12} sm={3}>
              <TextField
                fullWidth
                label={t("clientInfo")}
                variant="outlined"
                onChange={(e) => {
                  if (e.target.value) {
                    setSearchTerms({
                      ...searchTerms,
                      lcClient: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    lcClient: undefined,
                  });
                }}
              ></TextField>
            </Grid>
            <Grid
              item
              container
              xs={12}
              sm={3}
              justifyContent="flex-start"
              direction="column"
            >
              <TextField
                fullWidth
                label={t("deviceList.chassisNumber")}
                variant="outlined"
                onChange={(e) => {
                  if (e.target.value) {
                    setSearchTerms({
                      ...searchTerms,
                      chassisNumber: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    chassisNumber: undefined,
                  });
                }}
              ></TextField>
            </Grid>
            <Grid item container xs={12} sm={3}>
              <TextField
                fullWidth
                label={t("geolocation")}
                variant="outlined"
                helperText={t("geolocationHelper")}
                onChange={(e) => {
                  if (e.target.value) {
                    setSearchTerms({
                      ...searchTerms,
                      geolocation: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    geolocation: undefined,
                  });
                }}
              ></TextField>
            </Grid>
            <Grid item container xs={12} sm={3}>
              <TextField
                fullWidth
                label={t("deviceList.color")}
                variant="outlined"
                onChange={(e) => {
                  if (e.target.value) {
                    setSearchTerms({
                      ...searchTerms,
                      color: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    color: undefined,
                  });
                }}
              ></TextField>
            </Grid>
            <Grid item container xs={12} sm={3}>
              <TextField
                fullWidth
                label={t("deviceList.model")}
                variant="outlined"
                onChange={(e) => {
                  if (e.target.value) {
                    setSearchTerms({
                      ...searchTerms,
                      model: e.target.value,
                    });
                    return;
                  }
                  setSearchTerms({
                    ...searchTerms,
                    model: undefined,
                  });
                }}
              ></TextField>
            </Grid>

            <Grid item xs={6}>
              <Button
                variant="outlined"
                onClick={() => {
                  _getDevices();
                }}
              >
                {t("databasesearch")}
              </Button>
            </Grid>
            <Grid item container spacing={3} xs={12} alignItems="center">
              {currentSearchTerms ? (
                <Grid item xs={12}>
                  <Typography>{t("currentSearchTerms")}:</Typography>
                </Grid>
              ) : null}
              {currentSearchTerms
                ? Object.keys(currentSearchTerms).map((k, i) =>
                    currentSearchTerms[k] ? (
                      <Grid item key={i}>
                        <Typography color="primary">{k}</Typography>
                        <Typography>{currentSearchTerms[k]}</Typography>
                      </Grid>
                    ) : null,
                  )
                : null}
              {currentSearchTerms ? (
                <Grid item>
                  {" "}
                  <Button
                    color="secondary"
                    onClick={() => {
                      window.location.reload();
                    }}
                  >
                    {t("clearAll")}
                  </Button>{" "}
                </Grid>
              ) : null}
              <Grid item container spacing={1} xs={12}>
                <Grid item xs={12} container spacing={1}>
                  <Grid item>
                    <Title>{t("quickFilters")}</Title>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setShowQuickFilters(!showQuickFilters);
                      }}
                    >
                      {showQuickFilters ? t("hide") : t("show")}
                    </Button>
                  </Grid>
                </Grid>

                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.serial")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            serialNumber: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          serialNumber: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.chassisNumber")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            chassisNumber: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          chassisNumber: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("clientInfo")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            lcClient: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          lcClient: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}

                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("geolocation")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            geolocation: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          geolocation: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.lcState")}
                      variant="outlined"
                      select
                      SelectProps={{
                        multiple: true,
                        open: quickLcStateOpen,
                        onOpen: () => setQuickLcStateOpen(true),
                        onClose: () => setQuickLcStateOpen(false),
                        value: quickFilters.lcStates,
                        renderValue: () => "",
                      }}
                      onChange={(e) => {
                        setQuickFilters({
                          ...quickFilters,
                          lcStates: e.target.value,
                        });
                        setQuickLcStateOpen(false);
                      }}
                    >
                      {lifeCycleStates.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          selected={false}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {quickFilters.lcStates?.length ? (
                      <Grid item xs={12}>
                        {quickFilters.lcStates.map((state) => (
                          <Button
                            key={state}
                            size="small"
                            endIcon={<ClearIcon fontSize="small" />}
                            onClick={() => {
                              setQuickFilters({
                                ...quickFilters,
                                lcStates: quickFilters.lcStates.filter(
                                  (value) => value !== state,
                                ),
                              });
                            }}
                          >
                            {getLCState(state) || state}
                          </Button>
                        ))}
                      </Grid>
                    ) : null}
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.lcLocation")}
                      variant="outlined"
                      select
                      SelectProps={{
                        multiple: true,
                        open: quickWarehouseOpen,
                        onOpen: () => setQuickWarehouseOpen(true),
                        onClose: () => setQuickWarehouseOpen(false),
                        value: quickFilters.lcWarehouses,
                        renderValue: () => "",
                      }}
                      onChange={(e) => {
                        setQuickFilters({
                          ...quickFilters,
                          lcWarehouses: e.target.value,
                        });
                        setQuickWarehouseOpen(false);
                      }}
                    >
                      {warehouseLocation?.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          selected={false}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {quickFilters.lcWarehouses?.length ? (
                      <Grid item xs={12}>
                        {quickFilters.lcWarehouses.map((warehouse) => {
                          const match = warehouseLocation?.find(
                            (item) => item.value === warehouse,
                          );
                          return (
                            <Button
                              key={warehouse}
                              size="small"
                              endIcon={<ClearIcon fontSize="small" />}
                              onClick={() => {
                                setQuickFilters({
                                  ...quickFilters,
                                  lcWarehouses:
                                    quickFilters.lcWarehouses.filter(
                                      (value) => value !== warehouse,
                                    ),
                                });
                              }}
                            >
                              {match ? match.label : warehouse}
                            </Button>
                          );
                        })}
                      </Grid>
                    ) : null}
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.color")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            color: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          color: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("deviceList.model")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            model: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          model: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <TextField
                      fullWidth
                      label={t("note")}
                      variant="outlined"
                      onChange={(e) => {
                        if (e.target.value) {
                          setQuickFilters({
                            ...quickFilters,
                            note: e.target.value,
                          });
                          return;
                        }
                        setQuickFilters({
                          ...quickFilters,
                          note: undefined,
                        });
                      }}
                    ></TextField>
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          onChange={(e, v) => {
                            setQuickFilters({
                              ...quickFilters,
                              hideReservations: v,
                            });
                          }}
                        />
                      }
                      label={t("deviceList.hideOrderReservations")}
                    />
                  </Grid>
                ) : null}
                {showQuickFilters ? (
                  <Grid item container xs={12} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color={"primary"}
                          onChange={(e, v) => {
                            setQuickFilters({
                              ...quickFilters,
                              onlyAllocated: v,
                            });
                          }}
                        />
                      }
                      label={t("deviceList.reservedForOrder")}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={1}>
              {searchList ? (
                <>
                  <Grid item>
                    <Typography color="primary">
                      {t("fetchedDevices")}
                    </Typography>
                    <Typography>{fetchedDeviceCount ?? ""}</Typography>
                  </Grid>
                  <Grid item>
                    <Typography color="primary">
                      {t("listedDevices")}
                    </Typography>
                    <Typography>{searchList?.length ?? ""}</Typography>
                  </Grid>
                  {selectedDeviceLoading ? (
                    <Grid item>
                      <CircularProgress size={24} />
                    </Grid>
                  ) : null}
                </>
              ) : null}
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>{t("deviceList.header")}</Title>
          {searchList ? (
            <SortableTable
              checkBox={true}
              tableLayout={"fixed"}
              headCells={[
                {
                  id: "serialNumber",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.serial"),
                },
                {
                  id: "chassisNumber",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.chassisNumber"),
                },
                {
                  id: "plugType",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.plugType"),
                },
                {
                  id: "color",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.color"),
                },
                {
                  id: "model",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.model"),
                },
                {
                  id: "location",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.location"),
                },
                {
                  id: "deviceType",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.type"),
                },
                {
                  id: "user",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.user"),
                },
                {
                  id: "latestTimestamp",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.timeStamp"),
                },
                {
                  id: "currentLCState",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.lcState"),
                },
                {
                  id: "currentLCLocation",
                  numeric: false,
                  disablePadding: false,
                  label: t("deviceList.lcLocation"),
                },
                checkRequiredPermissions([
                  USER_GROUPS.SUPER_USER,
                  USER_GROUPS.ADMIN,
                ])
                  ? {
                      id: "Order.number",
                      numeric: false,
                      disablePadding: false,
                      label: t("deviceList.reservedForOrder"),
                    }
                  : { id: "" },
                {
                  id: "clientInfo",
                  numeric: false,
                  disablePadding: false,
                  label: t("clientInfo"),
                },
                {
                  id: "note",
                  numeric: false,
                  disablePadding: false,
                  label: t("note"),
                },
              ]}
              specialDataFormatting={[
                {
                  id: "Order.number",
                  formatReturnsElement: true,
                  format: (d, dd, ddd) => {
                    if (dd && dd === "json" && d) {
                      return d;
                    }
                    return d ? (
                      <Link href={"/cpanel/orders/" + d}>{d}</Link>
                    ) : (
                      ""
                    );
                  },
                },
              ]}
              // disablePagination: true,
              defaultSortRow={"serialNumber"}
              handleRowClick={showDevice}
              // handleScrollClick = {showDeviceNewTab,
              header={
                t("mainSideBar.production") +
                "-" +
                new Date().toISOString().substring(0, 10)
              }
              tableData={searchList}
              downloadButton={true}
              rowsPerPage={50}
            />
          ) : loading ? (
            <CircularProgress />
          ) : undefined}
        </Paper>
      </Grid>
    </Grid>
  );
}
